// Raiz del portal: sin libreria de rutas ni state manager. La seccion y la
// planta filtrada viven en la URL (shell/useRoute.ts, PP-62), asi el login
// y la sesion vencida no las pierden: al volver a ingresar, el operador
// sigue donde estaba. Config faltante -> mensaje claro; sin sesion -> login;
// con sesion -> el portal.
import { useCallback, useMemo, useState } from "react";
import { Login } from "./auth/Login.js";
import { useRenovacionDeSesion } from "./auth/renewal.js";
import { borrarSesion, guardarSesion, leerSesion, type PortalSession } from "./auth/session.js";
import { Alert } from "./components/ui/alert.js";
import { Brand } from "./components/ui/brand.js";
import { Card } from "./components/ui/card.js";
import { readConfig } from "./config.js";
import { createHttpFleetPort } from "./fleet/http.js";
import { createHttpMultiSitePort } from "./multi-site/http.js";
import { createHttpNotificationsPort } from "./notifications/http.js";
import { Portal } from "./portal/Portal.js";
import { createHttpRequestsPort } from "./requests/http.js";
import { createHttpTripExportPort } from "./settlements/http.js";
import { createHttpSettlementPort } from "./settlements/settlement-http.js";
import { createHttpSitesPort } from "./sites/http.js";

export function App(): React.JSX.Element {
  const config = useMemo(() => readConfig(), []);
  const sitesPort = useMemo(() => config === null ? null : createHttpSitesPort(config.apiUrl), [config]);
  const fleetPort = useMemo(() => config === null ? null : createHttpFleetPort(config.apiUrl), [config]);
  const requestsPort = useMemo(() => config === null ? null : createHttpRequestsPort(config.apiUrl), [config]);
  const multiSitePort = useMemo(() => config === null ? null : createHttpMultiSitePort(config.apiUrl), [config]);
  const notificationsPort = useMemo(() => config === null ? null : createHttpNotificationsPort(config.apiUrl), [config]);
  const tripExportPort = useMemo(() => config === null ? null : createHttpTripExportPort(config.apiUrl), [config]);
  const settlementPort = useMemo(() => config === null ? null : createHttpSettlementPort(config.apiUrl), [config]);
  const [session, setSession] = useState<PortalSession | null>(() => leerSesion());
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const onLogin = useCallback((next: PortalSession) => {
    guardarSesion(next);
    setNotice(undefined);
    setSession(next);
  }, []);

  const onLogout = useCallback((reason: "logout" | "expired") => {
    borrarSesion();
    setNotice(reason === "expired" ? "La sesion expiro. Ingresa de nuevo." : undefined);
    setSession(null);
  }, []);

  // Renovacion silenciosa: la sesion se renueva sola antes de vencer y el
  // operador no cae al login mientras despacha.
  const alVencerSesion = useCallback(() => {
    onLogout("expired");
  }, [onLogout]);
  useRenovacionDeSesion(session, setSession, alVencerSesion);

  const cerrarSesion = useCallback(() => {
    onLogout("logout");
  }, [onLogout]);

  if (config === null || sitesPort === null || fleetPort === null || requestsPort === null || multiSitePort === null || notificationsPort === null || tripExportPort === null || settlementPort === null) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center p-6">
        <Card className="w-full max-w-[400px] gap-4 p-7">
          <Brand size="sm" />
          <h1 className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-texto">
            Portal del proveedor
          </h1>
          <Alert variant="error">
            Falta configuración de build: VITE_API_URL, VITE_COGNITO_USER_POOL_ID y
            VITE_COGNITO_CLIENT_ID. En desarrollo se definen en apps/portal/.env.local.
          </Alert>
        </Card>
      </div>
    );
  }

  if (session === null) {
    return notice === undefined ? (
      <Login config={config} onLogin={onLogin} />
    ) : (
      <Login config={config} notice={notice} onLogin={onLogin} />
    );
  }

  return (
    <Portal
      session={session}
      port={sitesPort}
      fleetPort={fleetPort}
      requestsPort={requestsPort}
      multiSitePort={multiSitePort}
      notificationsPort={notificationsPort}
      tripExportPort={tripExportPort}
      settlementPort={settlementPort}
      onLogout={cerrarSesion}
    />
  );
}
